import { type DocumentNode, NetworkStatus } from '@apollo/client';
import { createContext, useCallback, useContext, useEffect } from 'react';

import { Spinner } from 'components/ui';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import { useUniqueId } from 'utils/hooks';
import i18n from 'utils/i18n';

import { StyledTable } from './DataTable.style';
import { EmptyState } from './EmptyState';
import { type TQueryFilter, type TServerDataTableContext } from './types';
import { useServerData } from './useServerData';
import {
  customTableRowClassName,
  filtersHaveChanged,
  useServerDataTableContextState,
} from './utils';

import { type DataTableProps } from './index';

/**
 * "The danger is unleashed only if you substantially disturb this place physically.
 * This place is best shunned and left uninhabited."
 */
export const ServerDataTableContext = createContext<{
  context: TServerDataTableContext;
  setContext: React.Dispatch<React.SetStateAction<TServerDataTableContext>>;
}>({
  context: { tableFilters: [] },
  setContext: (_context) => {},
});

export function useServerDataTableContext() {
  return useContext(ServerDataTableContext);
}

interface ServerDataTableProps extends Omit<
  DataTableProps,
  | 'columns'
  | 'dataQuery'
  | 'hasServerDataTableContextProviderParent'
  | 'renderInServerContext'
  | 'title'
> {
  columns?: TTableColumnDef[];
  filterColumns?: TTableColumnDef[];
  dataQuery: DocumentNode;
  deferCount?: boolean;
  onTableChange: (pagination: any, filters: any, sorter: any, extra: any) => void;
}

// in order to turn our `refetch` into a async operation, we are keeping track
// of the queries loading and using an interval to resolve the promise.
// @see `refetchAwait` below
const loadingFlags: Record<string, boolean> = {};

/**
 * What is here was dangerous and repulsive to us. This message is a warning about danger
 * @param param0
 * @returns
 */
export const ServerDataTable: React.FunctionComponent<ServerDataTableProps> = ({
  columns,
  filterColumns,
  transform = undefined,
  dataQuery,
  onRow,
  onDataQueryChange,
  onTableChange,
  routeProps = {},
  deferCount = false,
  rowKey,
  loading: externalLoading,
  setLoading,
  setTotalCount,
  ...props
}) => {
  const uniqueId = useUniqueId();
  const { context, setContext } = useServerDataTableContext();

  const {
    client,
    networkStatus,
    data = [],
    refetch,
    filterBy,
    countLoading,
  } = useServerData({
    query: dataQuery,
    setLoading,
    setTotalCount,
    transform,
    columns: filterColumns ?? columns,
    routeProps,
    deferCount,
    onDataQueryChange,
    rowSelection: props.rowSelection,
  });

  const loading = [
    NetworkStatus.loading,
    NetworkStatus.setVariables,
    NetworkStatus.refetch,
  ].includes(networkStatus);

  // just in case there are multiple ServerDataTable nodes, we don't want to
  // "cross the streams" with our loading flags, so we use a unique id to
  // nicely separate them
  loadingFlags[uniqueId] = loading;

  const refetchAwait = useCallback(
    (variables?: Partial<any>) => {
      // limit our interval to 1000 iterations (at 30ms)
      let count = 1000;

      return new Promise<void>((resolve) => {
        // by using an interval here and polling the state of the loading flag,
        // we can then resolve the promise once the loading is no longer `true`.
        // note that the context of the loading flag(s) are within this file, not
        // component, so there's only 1 instance of the loading flags object
        const int = setInterval(() => {
          if (count-- <= 0 || !loadingFlags[uniqueId]) {
            clearInterval(int);
            resolve();
          }
        }, 30);

        refetch(variables);
      });
    },
    [refetch, uniqueId]
  );

  useEffect(() => {
    if (context && filterBy) {
      if (filtersHaveChanged(context.tableFilters ?? [], filterBy as TQueryFilter[])) {
        setContext({ ...context, tableFilters: filterBy });
      }
    }
  }, [filterBy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (setLoading) {
      setLoading(loading);
    }
  }, [loading, setLoading]);

  // set loading state to be dependent on dataQuery loading or externalLoading
  const tableLoading = loading || Boolean(externalLoading);

  useEffect(() => {
    setContext((context) => ({ ...context, client, loading: tableLoading, refetch, refetchAwait }));
  }, [client, refetch, refetchAwait, setContext, tableLoading]);

  // A same-variables refetch is the only fetch that leaves still-valid rows in `data`; blanking
  // them collapses the table body, which resets scroll position and rebuilds the `scroll.y`
  // container. Every other fetch already returns no data, so it needs no guard here.
  // for most implementations, we want to remove null values.
  // Implementations that do not want this can replace a null value with empty object.
  const dataSource =
    !tableLoading || networkStatus === NetworkStatus.refetch ? (data || []).filter(Boolean) : [];

  return (
    <StyledTable
      {...props}
      loading={tableLoading}
      locale={{ emptyText: <EmptyState {...props} loading={tableLoading} /> }}
      columns={columns}
      dataSource={dataSource}
      bordered={false}
      rowKey={rowKey}
      onRow={onRow}
      onChange={(...args) => onTableChange(...args)}
      rowClassName={customTableRowClassName}
      pagination={
        deferCount
          ? {
              ...props.pagination,
              showTotal: (total, range) =>
                countLoading ? (
                  <Spinner size={1} containerHeight="unset" />
                ) : (
                  i18n.t('table.paginationTotal', { start: range[0], end: range[1], total })
                ),
            }
          : props.pagination
      }
    />
  );
};

interface ServerDataTableContextProviderProps {
  children: React.ReactNode;
  defaultState?: Partial<TServerDataTableContext>;
}

export function ServerDataTableContextProvider({
  children,
  defaultState,
}: ServerDataTableContextProviderProps) {
  const { context, setContext } = useServerDataTableContextState(defaultState);

  return (
    <ServerDataTableContext.Provider value={{ context, setContext }}>
      {children}
    </ServerDataTableContext.Provider>
  );
}
